import React, { useEffect, useMemo } from 'react';
import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { Avatar } from '@mui/material';
import { getOGDPerfomers } from '../../../../../utils/constants/pointColors';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getSpecialPerformers } from '../../../../../redux/features/poinTrackerSlice';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

export default function OGDPerformers({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.specialPerformers);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getSpecialPerformers(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const formatNumber = (val) => {
    if (val === null || val === undefined) return '-';
    return Math.round(Number(val));
  };

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const OGDSpecialPerformers = responseData?.data ?? {};

  const performers = [
    {
      title: OGDSpecialPerformers?.salesTitan?.category,
      name: OGDSpecialPerformers?.salesTitan?.name,
      value: OGDSpecialPerformers?.salesTitan?.mainMetricValue,
      label: OGDSpecialPerformers?.salesTitan?.secondaryMetricLabel,
      percent: OGDSpecialPerformers?.salesTitan?.secondaryMetricValue,
      growth: OGDSpecialPerformers?.salesTitan?.trend,
      teamName: OGDSpecialPerformers?.salesTitan?.team,
    },
    {
      title: OGDSpecialPerformers?.goalCrasher?.category,
      name: OGDSpecialPerformers?.goalCrasher?.name,
      value: OGDSpecialPerformers?.goalCrasher?.mainMetricValue,
      label: OGDSpecialPerformers?.goalCrasher?.secondaryMetricLabel,
      percent: OGDSpecialPerformers?.goalCrasher?.secondaryMetricValue,
      growth: OGDSpecialPerformers?.goalCrasher?.trend,
      teamName: OGDSpecialPerformers?.goalCrasher?.team,
    },
    {
      title: OGDSpecialPerformers?.bigDealMaestro?.category,
      name: OGDSpecialPerformers?.bigDealMaestro?.name,
      value: OGDSpecialPerformers?.bigDealMaestro?.mainMetricValue,
      label: OGDSpecialPerformers?.bigDealMaestro?.secondaryMetricLabel,
      percent: OGDSpecialPerformers?.bigDealMaestro?.secondaryMetricValue,
      growth: OGDSpecialPerformers?.bigDealMaestro?.trend,
      teamName: OGDSpecialPerformers?.bigDealMaestro?.team,
    },
    {
      title: OGDSpecialPerformers?.admissionGuru?.category,
      name: OGDSpecialPerformers?.admissionGuru?.name,
      value: OGDSpecialPerformers?.admissionGuru?.mainMetricValue,
      label: OGDSpecialPerformers?.admissionGuru?.secondaryMetricLabel,
      percent: OGDSpecialPerformers?.admissionGuru?.secondaryMetricValue,
      growth: OGDSpecialPerformers?.admissionGuru?.trend,
      teamName: OGDSpecialPerformers?.admissionGuru?.team,
    },
  ];

  const noData =
    !loading &&
    (!OGDSpecialPerformers || Object.keys(OGDSpecialPerformers).length === 0 || performers.every((p) => !p.name));

  return (
    <Box>
      {/* Title */}
      {loading ? (
        <Skeleton width="40%" height={25} sx={{ mb: 2 }} />
      ) : (
        <Typography fontWeight="bold" mb={2}>
          OGD Special Performers
        </Typography>
      )}

      {/* Cards Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr', // mobile
            sm: '1fr 1fr', // tablet
            md: '1fr 1fr', // desktop
          },
          justifyContent: 'flex-start',
          rowGap: 2,
          columnGap: 2,
        }}
      >
        {loading ? (
          [...Array(4)].map((_, index) => (
            <Card key={index} sx={{ p: 2, borderRadius: 2, minHeight: 120 }}>
              <Skeleton width="60%" height={20} />

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Skeleton variant="circular" width={50} height={50} />
                <Box sx={{ ml: 2, flex: 1 }}>
                  <Skeleton width="70%" height={20} />
                  <Skeleton width="40%" height={25} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Box>
                  <Skeleton width={40} />
                  <Skeleton width={60} />
                </Box>
                <Skeleton width={50} />
              </Box>
            </Card>
          ))
        ) : noData ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">No Data Found</Typography>
          </Box>
        ) : (
          performers.map((item, index) => {
            const { bg } = getOGDPerfomers(item.title);
            return (
              <Card
                key={index}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  color: '#fff',
                  background: bg,
                  width: '100%',
                  height: 'auto',
                  minHeight: 70,
                }}
              >
                {/* Title */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                  <Icon icon="solar:star-outline" width="15" height="15" />

                  <Typography variant="body2">{item.title}</Typography>
                </Box>
                <Typography variant="caption" sx={{ justifyContent: 'center' }}>
                  {item.teamName}
                </Typography>
                {/* Profile + Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  {/* Circle Avatar */}
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontWeight: 'bold',
                      mr: 2,
                    }}
                  >
                    {item.name ? item.name.charAt(0) : '?'}
                  </Avatar>

                  <Box>
                    <Typography fontSize={14} fontWeight="bold">
                      {item.name}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(item.value)}
                    </Typography>
                  </Box>
                </Box>
                {/* Bottom Info */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 3,
                    fontSize: 12,
                  }}
                >
                  <Box>
                    <Typography variant="caption">{item.label}</Typography>
                    <Typography variant="caption" display="block">
                      {item.percent}
                    </Typography>
                  </Box>
                  {item.growth !== undefined && item.growth !== null && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {item.growth > 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'white' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 16, color: 'white' }} />
                      )}

                      <Typography variant="caption" color="white">
                        {item.growth}
                      </Typography>
                    </Stack>
                  )}
                  {/* <Typography variant="caption">↑ {item.growth}</Typography> */}
                </Box>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
}
